import { Button, Form, Input, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  type TargetPlatform,
  type WorkflowSummary,
  type WorkflowTemplate,
  type WorkflowType,
  workflowsApi,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';
import { formatDatetime } from '@/lib/formatDatetime';
import { message } from '@/lib/message';

import { deriveAppNameBase } from './deriveAppNameBase';
import { extractApiError } from './statusUtils';

type FormValues = {
  nameEn: string;
  nameFr: string;
  descriptionEn?: string;
  descriptionFr?: string;
  appName: string;
  type: WorkflowType;
  platform: TargetPlatform;
  includedDealerCodes: string[];
  excludedDealerCodes: string[];
};

const DEALER_CODE_PATTERN = /^[0-9]+$/;

type Props = {
  open: boolean;
  onClose: () => void;
  /** When set, the modal switches to edit mode (prefilled, save calls update). */
  /** Summary is enough — the modal only edits card metadata (names, appName,
   *  type, platform, dealer codes). Steps are edited on the details page. */
  editing?: WorkflowSummary | null;
  /**
   * Called after a successful create or save. Receives the updated/created template
   * so the parent can patch its local state instead of re-fetching from the eventually-
   * consistent byOwner GSI (which can briefly return stale rows).
   */
  onSaved?: (saved: WorkflowTemplate) => void;
};

/**
 * Workflow create / edit dialog. English title + application name + assignment
 * type are mandatory; French title + both descriptions are optional (ADR-0016).
 * App name is auto-derived from the English title and immutable after creation.
 */
export function CreateWorkflowModal({ open, onClose, editing, onSaved }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!editing;

  // Timestamp captured once when the modal opens in create mode so it stays
  // stable while the user edits the title.
  const openTimestamp = useRef('');

  // Auto-derive appName from the English title in create mode. `deriveAppNameBase`
  // lowercases + strips non-slug chars so the composed name satisfies the domain
  // regex `^[a-z][a-z0-9-]{2,63}$` — previously an uppercase title like
  // "BMO RDSP" produced "BMO-RDSP-<stamp>" and the Create call failed with
  // "Application Name (Smartform Name) is Invalid".
  const nameEn = Form.useWatch('nameEn', form);
  useEffect(() => {
    if (!isEdit) {
      const base = deriveAppNameBase(nameEn ?? '');
      form.setFieldValue('appName', base ? `${base}-${openTimestamp.current}` : '');
    }
  }, [nameEn, isEdit, form]);

  // Prefill when opening in edit mode; capture timestamp when opening in create mode.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        nameEn: editing.nameEn,
        nameFr: editing.nameFr,
        descriptionEn: editing.descriptionEn,
        descriptionFr: editing.descriptionFr,
        appName: editing.appName,
        type: editing.type,
        // Legacy workflows created before the field existed have no platform — default to APEX.
        platform: editing.platform ?? 'APEX',
        includedDealerCodes: editing.includedDealerCodes ?? [],
        excludedDealerCodes: editing.excludedDealerCodes ?? [],
      });
    } else {
      openTimestamp.current = formatDatetime(new Date());
      form.resetFields();
    }
  }, [open, editing, form]);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      let saved: WorkflowTemplate;
      if (editing) {
        // App name is immutable; carry the existing order through.
        saved = await workflowsApi.update(editing.id, {
          nameEn: values.nameEn,
          nameFr: values.nameFr,
          descriptionEn: values.descriptionEn,
          descriptionFr: values.descriptionFr,
          type: values.type,
          platform: values.platform,
          order: editing.order,
          includedDealerCodes: values.includedDealerCodes,
          excludedDealerCodes: values.excludedDealerCodes,
        });
        message.success(t('workflow.form.saved'));
      } else {
        saved = await workflowsApi.create({ order: 0, ...values });
        message.success(t('workflow.form.created'));
      }
      form.resetFields();
      onClose();
      onSaved?.(saved);
      if (!editing) {
        navigate(`/originator/workflows/${saved.id}`);
      }
    } catch (err) {
      const e = extractApiError(err);
      if (e.details?.length) {
        form.setFields(
          e.details
            .filter((d) => d.field)
            .map((d) => ({ name: d.field as keyof FormValues, errors: [d.message] })),
        );
      } else {
        message.error(e.message ?? t('workflow.form.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BmoModal
      open={open}
      onCancel={close}
      width={640}
      title={isEdit ? t('workflow.form.editTitle') : t('workflow.form.modalTitle')}
      footer={[
        <Button key="cancel" onClick={close}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" loading={submitting} onClick={handleSave}>
          {isEdit ? t('workflow.form.save') : t('common.create')}
        </Button>,
      ]}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={{ type: 'Both', includedDealerCodes: [], excludedDealerCodes: [] }}
      >
        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            name="nameEn"
            label={t('workflow.fields.titleEn')}
            rules={[{ required: true, message: t('workflow.form.titleEnRequired') }]}
            style={{ flex: 1 }}
          >
            <Input placeholder={t('workflow.form.titleEnPlaceholder')} />
          </Form.Item>
          <Form.Item
            name="nameFr"
            label={t('workflow.fields.titleFr')}
            rules={[{ required: true, message: t('workflow.form.titleFrRequired') }]}
            style={{ flex: 1 }}
          >
            <Input placeholder={t('workflow.form.titleFrPlaceholder')} />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            name="descriptionEn"
            label={t('workflow.fields.descEnOptional')}
            extra={t('workflow.form.descEnHint')}
            style={{ flex: 1 }}
          >
            <Input.TextArea
              rows={4}
              maxLength={500}
              style={{ resize: 'none' }}
              placeholder={t('workflow.form.descEnPlaceholder')}
            />
          </Form.Item>
          <Form.Item
            name="descriptionFr"
            label={t('workflow.fields.descFrOptional')}
            extra={t('workflow.form.descFrHint')}
            style={{ flex: 1 }}
          >
            <Input.TextArea
              rows={4}
              maxLength={500}
              style={{ resize: 'none' }}
              placeholder={t('workflow.form.descFrPlaceholder')}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="appName"
          label={t('workflow.fields.appNameSmartform')}
          extra={t('workflow.form.appNameHint')}
          style={{ marginBottom: 0 }}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('workflow.fields.assignmentType')}
          rules={[{ required: true, message: t('workflow.form.required') }]}
        >
          <BmoRadioGroup<WorkflowType>
            options={[
              { label: t('workflow.types.internal'), value: 'InternalAdvisor' },
              { label: t('workflow.types.external'), value: 'ExternalAdvisor' },
              { label: t('workflow.types.both'), value: 'Both' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="platform"
          label={t('workflow.fields.platform')}
          rules={[{ required: true, message: t('workflow.form.required') }]}
        >
          <BmoRadioGroup<TargetPlatform>
            options={[
              { label: t('workflow.platforms.IFDS'), value: 'IFDS' },
              { label: t('workflow.platforms.APEX'), value: 'APEX' },
            ]}
          />
        </Form.Item>

        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            name="includedDealerCodes"
            label={t('workflow.fields.includedDealerCodes')}
            extra={t('workflow.form.includedDealerCodesHint')}
            rules={[{ validator: dealerCodeListValidator(t('workflow.form.dealerCodeInvalid')) }]}
            style={{ flex: 1 }}
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',']}
              placeholder={t('workflow.form.dealerCodePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            name="excludedDealerCodes"
            label={t('workflow.fields.excludedDealerCodes')}
            extra={t('workflow.form.excludedDealerCodesHint')}
            rules={[{ validator: dealerCodeListValidator(t('workflow.form.dealerCodeInvalid')) }]}
            style={{ flex: 1 }}
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',']}
              placeholder={t('workflow.form.dealerCodePlaceholder')}
            />
          </Form.Item>
        </div>
      </Form>
    </BmoModal>
  );
}

/** Rejects any tag that isn't digits-only — the chiclet list accepts free-text
 *  tags (comma-paste splits via `tokenSeparators`), so format isn't enforced
 *  until validation runs. */
function dealerCodeListValidator(message: string) {
  return (_: unknown, value?: string[]) => {
    if (value?.some((code) => !DEALER_CODE_PATTERN.test(code))) {
      return Promise.reject(new Error(message));
    }
    return Promise.resolve();
  };
}
